/**
 * Transfer History Tab - Records of clinician/client transfers for this center
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import locationTransferService, {
  TransferHistoryEntry,
} from '../../services/api/locationTransferService';

interface TransferHistoryTabProps {
  locationId: string;
}

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FilterWrap = styled.div`
  min-width: 160px;
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RouteCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const RouteArrow = styled.span`
  color: ${({ theme }) => theme.colors.text.light};
`;

const ReasonCell = styled.div`
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ==================== Constants ====================

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'clinician', label: 'Clinicians' },
  { value: 'client', label: 'Clients' },
];

// ==================== Helpers ====================

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// ==================== Component ====================

export const TransferHistoryTab = ({ locationId }: TransferHistoryTabProps) => {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<'' | 'clinician' | 'client'>('');

  const { data, isLoading } = useQuery({
    queryKey: ['location-transfer-history', locationId, page, typeFilter],
    queryFn: () =>
      locationTransferService.getTransferHistory(locationId, {
        page,
        limit: 15,
        type: typeFilter || undefined,
      }),
  });

  const entries = data?.data ?? [];
  const meta = data?.meta;

  return (
    <Container>
      <ToolbarRow>
        <SectionTitle>Transfer History</SectionTitle>
        <FilterWrap>
          <Select
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as '' | 'clinician' | 'client');
              setPage(1);
            }}
            fullWidth
          />
        </FilterWrap>
      </ToolbarRow>

      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Type</Th>
              <Th>Name</Th>
              <Th>Route</Th>
              <Th>Reason</Th>
              <Th>Initiated By</Th>
              <Th>Date</Th>
            </tr>
          </Thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <Td><SkeletonBar $width="60px" /></Td>
                  <Td><SkeletonBar $width="130px" /></Td>
                  <Td><SkeletonBar $width="200px" /></Td>
                  <Td><SkeletonBar $width="120px" /></Td>
                  <Td><SkeletonBar $width="110px" /></Td>
                  <Td><SkeletonBar $width="80px" /></Td>
                </tr>
              ))
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState>
                    {typeFilter
                      ? `No ${typeFilter} transfers found.`
                      : 'No transfers recorded for this center.'}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              entries.map((entry: TransferHistoryEntry) => (
                <Tr key={entry.id}>
                  <Td>
                    <Badge
                      variant={entry.entityType === 'clinician' ? 'primary' : 'success'}
                    >
                      {entry.entityType === 'clinician' ? 'Clinician' : 'Client'}
                    </Badge>
                  </Td>
                  <Td>{entry.entityName}</Td>
                  <Td>
                    <RouteCell>
                      <SecondaryText>{entry.fromCenterName}</SecondaryText>
                      <RouteArrow>→</RouteArrow>
                      <SecondaryText>{entry.toCenterName}</SecondaryText>
                    </RouteCell>
                  </Td>
                  <Td>
                    <ReasonCell title={entry.reason}>
                      <SecondaryText>{entry.reason || '—'}</SecondaryText>
                    </ReasonCell>
                  </Td>
                  <Td>
                    <SecondaryText>{entry.initiatedByName}</SecondaryText>
                  </Td>
                  <Td>
                    <SecondaryText>{formatDate(entry.createdAt)}</SecondaryText>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>

        {meta && meta.totalPages > 1 && (
          <Pagination>
            <PaginationInfo>
              Page {meta.page} of {meta.totalPages} ({meta.total} records)
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </PageButton>
              <PageButton
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= meta.totalPages}
                aria-label="Next page"
              >
                <ChevronRight />
              </PageButton>
            </PaginationControls>
          </Pagination>
        )}
      </TableWrapper>
    </Container>
  );
};
